import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import * as React from 'react'

import { ErrorBoundary } from 'react-error-boundary'

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { AxiosError } from 'axios'

import { Toaster } from 'sonner'

import { logger } from '@/lib/logger'

import { paths } from '@/config/paths'

import { AuthProvider } from '@/context/AuthProvider'

import { MainErrorFallback } from '@/components/errors/MainErrorFallback'

import { InAppBrowserBanner } from '@/components/InAppBrowserBanner'
import LoadingSpinner from '@/components/LoadingSpinner'

import i18n from '@/i18n'
import MaintenancePage from '@/pages/MaintenancePage'

import toast from '../components/toast'
import { useAuthStore } from '../stores/auth'
import { useMaintenanceStore } from '../stores/useMaintenanceStore'
import BranchChangeHandler from './BranchChangeHandler'
import { DynamicFavicon } from './DynamicFavicon'

type AppProviderProps = {
  children: React.ReactNode
}

// Centralized error handler
const handleGlobalError = (error: unknown) => {
  if (!(error instanceof AxiosError)) return

  const status = error.response?.status ?? 0
  const { user, logout, setUser } = useAuthStore.getState()

  switch (status) {
    case 401:
      toast.error(i18n.t('common:errors.sessionExpired', 'Session expired!'))
      if (user) logout()
      setUser(null)
      window.location.href = paths.public.auth.login.path // or use navigate(paths.public.auth.login.path)
      break

    case 403:
      toast.error(i18n.t('common:errors.accessDenied', 'Access Denied!'))
      window.location.href = paths.public.unauthorized.path
      break

    case 500:
      toast.error(i18n.t('common:errors.somethingWentWrong', 'Something went wrong! Please try again.'))
      // Only redirect if critical
      if (isCriticalRequest(error.config?.url)) {
        window.location.href = paths.public.serverError.path
      }
      break

    default:
      logger.error('Unhandled error', error)
  }
}

// Detect critical endpoints (initial app load, main API calls, etc.)
const isCriticalRequest = (url?: string) => {
  if (!url) return false
  return ['/auth/user', '/app-settings'].some((endpoint) => url.includes(endpoint))
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const isMaintenance = useMaintenanceStore((state) => state.isMaintenance)

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          logger.debug('AppProvider QueryClient', error)
          if (import.meta.env.DEV) return false // disable retries in dev
          if (failureCount > 3) return false
          // Don't retry on maintenance mode
          return !(error instanceof AxiosError && [401, 403, 503].includes(error.response?.status ?? 0))
        },
        refetchOnWindowFocus: import.meta.env.PROD,
        staleTime: 10 * 1000, // 10s
      },
      mutations: {
        onError: (error) => {
          if (error instanceof AxiosError && error.response?.status === 304) {
            toast.error(i18n.t('common:errors.contentNotModified', 'Content not modified!'))
          }
        },
      },
    },
    queryCache: new QueryCache({
      onError: handleGlobalError,
    }),
  })

  return (
    <ErrorBoundary FallbackComponent={MainErrorFallback}>
      <Toaster position='top-right' richColors closeButton />
      <InAppBrowserBanner />
      <QueryClientProvider client={queryClient}>
        {import.meta.env.DEV && <ReactQueryDevtools />}
        <MaintenancePoller />
        {isMaintenance ? (
          <MaintenancePage />
        ) : (
          <AuthProvider>
            <DynamicFavicon />
            <BranchChangeHandler />
            <React.Suspense fallback={<LoadingSpinner />}>{children}</React.Suspense>
          </AuthProvider>
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

// Component to poll for maintenance status and auto-recover
function MaintenancePoller() {
  const { isMaintenance, startPolling } = useMaintenanceStore()

  React.useEffect(() => {
    const interval = isMaintenance ? 10000 : 60000 // 10s in maintenance, 60s otherwise

    const cleanup = startPolling(interval)
    return cleanup
  }, [isMaintenance, startPolling])

  return null
}
